import { useEffect, useState, useCallback } from "react";
import { api, BREEDS, BREED_LABELS, MULTIPLIERS, fmt, errMsg, API, openServerFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, EmptyRow } from "@/components/shared";
import { F, In, Sel, DelBtn, Td, TableCard } from "@/pages/Feed";
import { toast } from "sonner";
import { ArrowLeftRight, FileText } from "lucide-react";

export default function Transfer() {
  const { coops, coopName, meta } = useApp();
  const locations = coops.filter((c) => c.type === "location" || c.type === "warehouse");
  const eggSizes = meta.egg_sizes;
  const defects = meta.defects;
  const [transfers, setTransfers] = useState([]);
  const [stock, setStock] = useState([]);
  const [form, setForm] = useState({ from_id: "", to_id: "", date: new Date().toISOString().slice(0, 10), waybill_no: "", plate: "", driver: "", note: "" });
  const [activeBreed, setActiveBreed] = useState("kirmizi");
  const [qty, setQty] = useState({}); // { fromCoop: { breed|'_fire': { size: val } } }

  const load = useCallback(async () => {
    const [t, st] = await Promise.all([api.get("/transfers"), api.get("/stock")]);
    setTransfers(t.data); setStock(st.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const ac = form.from_id;
  const gq = (k, size) => qty[ac]?.[k]?.[size] ?? "";
  const setQ = (k, size, val) => setQty((p) => ({ ...p, [ac]: { ...(p[ac] || {}), [k]: { ...((p[ac] || {})[k] || {}), [size]: val } } }));
  const eggStock = (size) => stock.find((s) => s.coop_id === ac && s.breed === activeBreed)?.sizes?.[size] ?? 0;
  const fireStock = (size) => stock.filter((s) => s.coop_id === ac).reduce((a, d) => a + (d.sizes?.[size] || 0), 0);
  const coopsWithItems = Object.keys(qty).filter((c) => Object.values(qty[c] || {}).some((m) => Object.values(m || {}).some((v) => Number(v) > 0)));

  const buildItems = () => {
    const items = [];
    Object.entries(qty).forEach(([coop, keys]) => Object.entries(keys).forEach(([k, sizes]) => Object.entries(sizes).forEach(([size, v]) => {
      const c = Number(v || 0);
      if (c <= 0) return;
      if (k === "_fire") items.push({ from_id: coop, size, cartons: c, is_fire: true });
      else items.push({ from_id: coop, breed: k, size, cartons: c, is_fire: false });
    })));
    return items;
  };
  const grandTotal = buildItems().reduce((s, i) => s + i.cartons, 0);

  const submit = async (force = false) => {
    if (!form.to_id) return toast.error("Varış lokasyonu seçin");
    const items = buildItems();
    if (items.length === 0) return toast.error("En az bir kalem girin");
    if (items.some((i) => i.from_id === form.to_id)) return toast.error("Çıkış ve varış aynı olamaz");
    try {
      await api.post(`/transfers?force=${force}`, { ...form, items });
      toast.success("Transfer kaydedildi, stoklar güncellendi");
      setForm({ ...form, waybill_no: "", plate: "", driver: "", note: "" });
      setQty({});
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.shortages) {
        const list = detail.shortages.map((s) => `${s.coop_name ? s.coop_name + " · " : ""}${s.breed_label} ${s.label}: ist. ${s.requested} / stok ${s.available}`).join("\n");
        if (window.confirm(`Yetersiz stok!\n${list}\n\nYine de eksi stoğa düşerek kaydedilsin mi?`)) submit(true);
        else toast.error("Transfer iptal edildi");
      } else toast.error(errMsg(e));
    }
  };

  const trBreeds = (t) => {
    const bs = t.breeds?.length ? t.breeds : (t.breed ? [t.breed] : []);
    const labels = [...new Set(bs)].map((b) => BREED_LABELS[b] || b);
    if ((t.items || []).some((i) => i.is_fire)) labels.push("Fire");
    return labels.join(", ") || "-";
  };
  const trSources = (t) => {
    const ids = [...new Set((t.items || []).map((i) => i.from_id || t.from_id))];
    return ids.map((id) => coopName(id)).join(", ") || (t.from_name || coopName(t.from_id));
  };

  return (
    <div>
      <PageHeader title="Depolar Arası Sevkiyat" subtitle="Çoklu kümesten tek transfer fişi · fire yumurta ırk bağımsız (4 kalite tipi) · çıkıştan düşer, varışa eklenir" />

      <div className="rounded-xl border border-border bg-card p-5 mb-6" data-testid="transfer-form">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          <F label="Çıkış Deposu / Kümes"><Sel value={form.from_id} onChange={(v) => setForm({ ...form, from_id: v })} opts={locations} testid="tr-from" /></F>
          <F label="Varış Deposu / Kümes"><Sel value={form.to_id} onChange={(v) => setForm({ ...form, to_id: v })} opts={locations.filter((l) => l.id !== form.from_id)} testid="tr-to" /></F>
          <F label="Tarih"><In type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="tr-date" /></F>
          <F label="İrsaliye / Transfer No"><In value={form.waybill_no} onChange={(v) => setForm({ ...form, waybill_no: v })} testid="tr-waybill" /></F>
          <F label="Araç / Plaka"><In value={form.plate} onChange={(v) => setForm({ ...form, plate: v })} testid="tr-plate" /></F>
        </div>

        {coopsWithItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="tr-coops-summary">
            <span className="text-muted-foreground">Fişteki çıkış kümesleri:</span>
            {coopsWithItems.map((c) => (
              <button key={c} onClick={() => setForm({ ...form, from_id: c })} className={`px-2.5 py-1 rounded-full font-semibold transition-colors ${ac === c ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/70"}`}>{coopName(c)}</button>
            ))}
          </div>
        )}

        {!ac ? (
          <div className="rounded-lg border border-dashed border-border bg-secondary/20 py-6 text-center text-sm text-muted-foreground mb-4">Kalem eklemek için bir <strong>Çıkış Kümesi</strong> seçin. Farklı kümeslerden mal eklemek için çıkışı değiştirip devam edin (tek fişe eklenir).</div>
        ) : (
          <>
            <div className="flex gap-2 mb-3">
              {BREEDS.map((b) => (
                <button key={b} onClick={() => setActiveBreed(b)} data-testid={`tr-breed-tab-${b}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeBreed === b ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                  {BREED_LABELS[b]}
                </button>
              ))}
            </div>
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">{coopName(ac)} · {BREED_LABELS[activeBreed]} — Standart Yumurta</div>
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-11 border border-border rounded-lg overflow-hidden mb-4">
              {eggSizes.map((s) => (
                <div key={s.key} className="border-r border-b border-border last:border-r-0">
                  <div className="px-2 py-1 text-[11px] font-semibold text-center bg-secondary/60 border-b border-border">{s.label}
                    <span className="block text-[10px] font-normal text-muted-foreground">stok: {fmt(eggStock(s.key))}</span>
                  </div>
                  <input type="number" min="0" className="grid-input text-center h-8 px-1 text-sm w-full bg-background outline-none" value={gq(activeBreed, s.key)} onChange={(e) => setQ(activeBreed, s.key, e.target.value)} data-testid={`tr-size-${activeBreed}-${s.key}`} />
                </div>
              ))}
            </div>
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">{coopName(ac)} · Fire Yumurta (ırk ayrımı yok — kalite/hasar tipine göre)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 border border-amber-500/30 rounded-lg overflow-hidden mb-4">
              {defects.map((s) => (
                <div key={s.key} className="border-r border-b border-border last:border-r-0">
                  <div className="px-2 py-1 text-[11px] font-semibold text-center bg-amber-500/10 border-b border-border">{s.label}
                    <span className="block text-[10px] font-normal text-muted-foreground">stok: {fmt(fireStock(s.key))}</span>
                  </div>
                  <input type="number" min="0" className="grid-input text-center h-8 px-1 text-sm w-full bg-background outline-none" value={gq("_fire", s.key)} onChange={(e) => setQ("_fire", s.key, e.target.value)} data-testid={`tr-fire-${s.key}`} />
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex items-center gap-3 pt-3 border-t border-border">
          <F label="Açıklama"><In value={form.note} onChange={(v) => setForm({ ...form, note: v })} testid="tr-note" /></F>
          <div className="ml-auto self-end flex items-center gap-4">
            <div className="text-sm text-muted-foreground">Genel Toplam: <strong className="text-foreground tabular-nums" data-testid="tr-grand-total">{fmt(grandTotal)}</strong> karton</div>
            <button onClick={() => submit(false)} data-testid="tr-save" className="h-9 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 text-sm font-semibold hover:bg-primary/90 transition-colors"><ArrowLeftRight size={16} /> Transferi Kaydet</button>
          </div>
        </div>
      </div>

      <TableCard headers={["Tarih", "Çıkış", "Varış", "Çeşitler", "Toplam Krt.", "İrsaliye", "Plaka", ""]}>
        {transfers.length === 0 ? <EmptyRow colSpan={8} text="Transfer kaydı yok." /> : transfers.map((t) => (
          <tr key={t.id} className="border-t border-border hover:bg-secondary/30" data-testid={`tr-row-${t.id}`}>
            <Td>{t.date}</Td>
            <Td className="font-medium">{trSources(t)}</Td>
            <Td className="font-medium">{t.to_name || coopName(t.to_id)}</Td>
            <Td>{trBreeds(t)}</Td>
            <Td className="text-right tabular-nums font-semibold">{fmt(t.total)}</Td>
            <Td>{t.waybill_no}</Td>
            <Td>{t.plate}</Td>
            <Td>
              <div className="flex items-center gap-2">
                <button onClick={() => openServerFile(`${API}/transfers/${t.id}/waybill?token=${localStorage.getItem("token")}`, `Transfer İrsaliyesi PDF`)} data-testid={`tr-waybill-${t.id}`} className="text-muted-foreground hover:text-primary transition-colors" title="Transfer İrsaliyesi PDF"><FileText size={16} /></button>
                <DelBtn onClick={async () => { await api.delete(`/transfers/${t.id}`); load(); }} />
              </div>
            </Td>
          </tr>
        ))}
      </TableCard>
    </div>
  );
}
